import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Bio, experiences } from '../../data/constants';
import { Tilt } from 'react-tilt';
import { BlinkingCursor } from '../../utils/CommonStyles';

// Add animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// const typingEffect = keyframes`
//   from { width: 0 }
//   to { width: 100% }
// `;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Enhanced Container styling
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 50px 0px 100px 0px;
  background: linear-gradient(
    343deg,
    rgba(17, 20, 28, 1) 0%,
    rgba(25, 28, 38, 1) 50%,
    rgba(17, 20, 28, 1) 100%
  );
  clip-path: polygon(0 0, 100% 0, 100% 100%, 70% 95%, 0 100%);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent 2px,
      rgba(0, 30, 60, 0.05) 2px,
      rgba(0, 30, 60, 0.05) 4px
    );
    pointer-events: none;
    opacity: 0.5;
    z-index: -1;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
`;

// Style the title section
const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 52px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.text_primary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;

    &:after {
      width: 120px;
    }
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Terminal window styling
const TerminalWindow = styled.div`
  width: 100%;
  max-width: 950px;
  margin-top: 20px;
  border-radius: 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.primary + '50'};
  position: relative;
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #262626;
  padding: 12px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.primary + '40'};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const TerminalDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 5px ${props => props.color};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ddd;
  letter-spacing: 1px;
`;

const TerminalBody = styled.div`
  padding: 25px;
  font-family: 'Courier New', monospace;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #2d2d2d;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.primary + '80'};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.primary};
    }
  }
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #ddd;
  display: flex;

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

// Experience card styling
const ExperienceCard = styled.div`
  background: rgba(45, 45, 45, 0.5);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid transparent;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.5s ease-out;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.primary + '50'},
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.primary + '50'};
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.3),
      0 0 15px ${({ theme }) => theme.primary + '30'} inset;
    background-color: rgba(50, 50, 50, 0.9);

    &:before {
      transform: translateX(100%);
    }
  }
`;

const ExperienceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const CompanyLogo = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: transform 0.3s ease;

  ${ExperienceCard}:hover & {
    transform: scale(1.1);
  }
`;

const ExperienceInfo = styled.div`
  flex: 1;
`;

const Role = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 4px;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
  }

  ${ExperienceCard}:hover & {
    &:after {
      width: 100%;
    }
  }
`;

const Company = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 4px;
`;

const Date = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
`;

const Description = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  margin-bottom: 15px;
  position: relative;
  padding-left: 15px;

  &:before {
    content: '>';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
`;

const Skill = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '15'};
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${({ theme }) => theme.primary + '25'};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const Experience = () => {
  return (
    <Container id="Experience">
      <Wrapper>
        <TitleSection>
          <Title>Experience</Title>
          <Desc>My professional journey and the impact I've made along the way</Desc>
        </TitleSection>

        <TerminalWindow>
          <ScanLine />
          <TerminalHeader>
            <TerminalDot color="#ff5f56" />
            <TerminalDot color="#ffbd2e" />
            <TerminalDot color="#27c93f" />
            <TerminalTitle>
              {Bio.name.toLowerCase().replaceAll(' ', '')}@backend-dev ~ /experience
            </TerminalTitle>
          </TerminalHeader>

          <TerminalBody>
            <TerminalLine>cat experience.json</TerminalLine>
            <CommandOutput>
              {experiences.map((experience, index) => (
                <Tilt key={index} options={{ max: 15, scale: 1.02, perspective: 1000 }}>
                  <ExperienceCard>
                    <ExperienceHeader>
                      <CompanyLogo src={experience.img} alt={experience.company} />
                      <ExperienceInfo>
                        <Role>{experience.role}</Role>
                        <Company>{experience.company}</Company>
                        <Date>{experience.date}</Date>
                      </ExperienceInfo>
                    </ExperienceHeader>
                    <Description>{experience.desc}</Description>
                    {experience.skills && (
                      <SkillsContainer>
                        {experience.skills.map((skill, idx) => (
                          <Skill key={idx}>{skill}</Skill>
                        ))}
                      </SkillsContainer>
                    )}
                  </ExperienceCard>
                </Tilt>
              ))}
            </CommandOutput>

            <TerminalLine>echo $CURRENT_STATUS</TerminalLine>
            <CommandOutput>
              <div style={{ color: '#ddd' }}>
                "Always learning, always growing, and always ready for the next challenge"
              </div>
            </CommandOutput>

            <TerminalLine>
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </TerminalBody>
        </TerminalWindow>
      </Wrapper>
    </Container>
  );
};

export default Experience;
